// Presentational notification row shared by the dropdown and the full screen.
// Tapping deep-links to the notification's target and marks it read. Each row
// carries its type's soft colour + icon.
//
// Right-side actions (full row height):
//   - actions="dismiss" (dropdown): a single X that marks it read. Always shown
//     on mobile, revealed on hover on desktop.
//   - actions="row" (screen): "marcar como lida" (unread only) + "visitar"
//     (whenever there's a destination). Revealed on hover on desktop; on mobile
//     it appears only after the first tap on the row.

import React, { useEffect, useState } from "react";
import { ArrowRight, Check, X } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { resolveNotificationPath } from "./deepLink.js";
import { formatRelativeTime } from "./relativeTime.js";
import { getTypeVisual } from "./typeVisuals.js";
import { isUnread, type NotificationItem } from "./model.js";

interface NotificationRowProps {
  item: NotificationItem;
  onMarkRead: (id: string) => void;
  /** Called after navigation so parents can close the dropdown. */
  onNavigate?: () => void;
  /** Which right-side action set to render. */
  actions?: "dismiss" | "row";
  compact?: boolean;
}

// Dropdown actions: always visible on mobile, revealed on hover on desktop.
const DISMISS_REVEAL =
  "opacity-100 transition-opacity duration-200 md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100";

const NotificationRow: React.FC<NotificationRowProps> = ({
  item,
  onMarkRead,
  onNavigate,
  actions,
  compact = false,
}) => {
  const navigate = useNavigate();
  const { t } = useTranslation("features");
  const unread = isUnread(item);
  const { Icon, iconClass, bgClass } = getTypeVisual(item.type);
  const path = resolveNotificationPath(item.deepLinkData);

  // On touch devices (no hover) the page row's actions are hidden until the
  // first tap reveals them; on desktop they reveal on hover.
  const [canHover, setCanHover] = useState(true);
  const [revealed, setRevealed] = useState(false);
  useEffect(() => {
    setCanHover(window.matchMedia?.("(hover: hover)").matches ?? true);
  }, []);

  const markRead = () => {
    if (unread) onMarkRead(item.id);
  };

  const visit = () => {
    markRead();
    if (path) navigate(path);
    onNavigate?.();
  };

  const hasRowActions = unread || Boolean(path);

  const handleMainClick = () => {
    // Page + touch: the first tap reveals the actions instead of navigating.
    if (actions === "row" && !canHover && hasRowActions && !revealed) {
      setRevealed(true);
      return;
    }
    visit();
  };

  // Page actions: hidden on mobile until revealed; hover/focus on desktop.
  const rowReveal = `transition-opacity duration-200 md:opacity-0 md:pointer-events-none md:group-hover:opacity-100 md:group-hover:pointer-events-auto md:focus-within:opacity-100 md:focus-within:pointer-events-auto ${
    revealed ? "opacity-100" : "opacity-0 pointer-events-none"
  }`;

  return (
    <div
      className={`group relative flex items-stretch transition-colors duration-200 hover:bg-golden/10 ${
        unread ? "bg-golden/5" : "bg-white"
      }`}
    >
      <button
        type="button"
        onClick={handleMainClick}
        className="flex-1 min-w-0 text-left flex items-start gap-3 px-4 py-3 focus:outline-none"
        aria-label={item.title}
      >
        <span
          className={`relative mt-0.5 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full ${bgClass}`}
        >
          <Icon className={`h-4 w-4 ${iconClass}`} aria-hidden="true" />
          {unread && (
            <span
              className="absolute -top-0.5 -right-0.5 h-2.5 w-2.5 rounded-full bg-main-purple ring-2 ring-white"
              aria-hidden="true"
            />
          )}
        </span>
        <span className="min-w-0 flex-1">
          <span
            className={`block truncate text-sm ${
              unread
                ? "font-semibold text-my-black"
                : "font-medium text-gray-700"
            }`}
          >
            {item.title}
          </span>
          <span
            className={`block text-sm text-gray-500 ${compact ? "truncate" : ""}`}
          >
            {item.body}
          </span>
          <span className="mt-1 block text-xs text-gray-400">
            {formatRelativeTime(item.createdAt)}
          </span>
        </span>
      </button>

      {actions === "dismiss" && (
        <button
          type="button"
          onClick={markRead}
          className={`flex w-11 items-center justify-center self-stretch border-l border-gray-100 text-gray-400 hover:bg-gray-100 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-golden ${DISMISS_REVEAL}`}
          aria-label={t("notifications.markRead")}
          title={t("notifications.markRead")}
        >
          <X className="h-4 w-4" aria-hidden="true" />
        </button>
      )}

      {actions === "row" && hasRowActions && (
        <div className={`flex items-stretch ${rowReveal}`}>
          {unread && (
            <button
              type="button"
              onClick={markRead}
              className="flex w-11 items-center justify-center self-stretch border-l border-gray-100 text-emerald-600 hover:bg-emerald-50 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-golden"
              aria-label={t("notifications.markRead")}
              title={t("notifications.markRead")}
            >
              <Check className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
          {path && (
            <button
              type="button"
              onClick={visit}
              className="flex w-11 items-center justify-center self-stretch border-l border-gray-100 text-main-purple hover:bg-main-purple/10 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-golden"
              aria-label={t("notifications.visit")}
              title={t("notifications.visit")}
            >
              <ArrowRight className="h-4 w-4" aria-hidden="true" />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationRow;
